// @react-pdf/renderer version of the Modern template.
// Produces a true vector PDF with selectable text, proper page breaks,
// and consistent layout across browsers — unlike window.print() which
// depends on the OS print dialog. ATS parsers extract this cleanly.

import React from 'react';
import { Document, Page, Text, View, StyleSheet } from '@react-pdf/renderer';

const styles = StyleSheet.create({
    page: {
        padding: 40,
        fontSize: 10,
        fontFamily: 'Helvetica',
        color: '#1e293b',
        lineHeight: 1.4
    },
    headerBlock: {
        borderBottomWidth: 2,
        borderBottomColor: '#1e293b',
        paddingBottom: 16,
        marginBottom: 20
    },
    name: {
        fontSize: 26,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        marginBottom: 8,
        color: '#0f172a'
    },
    contactRow: {
        flexDirection: 'row',
        flexWrap: 'wrap',
        fontSize: 9,
        color: '#475569'
    },
    contactItem: {
        marginRight: 10
    },
    section: {
        marginBottom: 16
    },
    sectionHeader: {
        fontSize: 9,
        fontWeight: 'bold',
        textTransform: 'uppercase',
        letterSpacing: 1.5,
        color: '#94a3b8',
        marginBottom: 8
    },
    summary: {
        fontSize: 10,
        lineHeight: 1.5,
        color: '#334155',
        textAlign: 'justify'
    },
    item: {
        marginBottom: 12
    },
    itemHeader: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        alignItems: 'flex-end',
        marginBottom: 2
    },
    itemTitle: {
        fontSize: 12,
        fontWeight: 'bold',
        color: '#1e293b',
        flex: 1
    },
    itemDate: {
        fontSize: 9,
        color: '#64748b'
    },
    itemSub: {
        fontSize: 10,
        fontWeight: 'bold',
        color: '#2563eb',
        marginBottom: 4
    },
    itemDesc: {
        fontSize: 9.5,
        lineHeight: 1.5,
        color: '#475569'
    },
    skillsRow: {
        flexDirection: 'row',
        flexWrap: 'wrap'
    },
    skillChip: {
        fontSize: 9,
        backgroundColor: '#f1f5f9',
        color: '#334155',
        paddingHorizontal: 8,
        paddingVertical: 3,
        borderRadius: 8,
        marginRight: 5,
        marginBottom: 5
    }
});

const splitBullets = (desc) => {
    if (typeof desc !== 'string' || !desc) return [];
    return desc.split(/\r?\n/).map(l => l.trim()).filter(Boolean);
};

// The section itself must wrap — a long Work Experience block is taller than a
// page. Only the individual items are kept whole (see `wrap={false}` below),
// and the header is glued to whatever follows it.
const Section = ({ title, children }) => (
    <View style={styles.section}>
        <View wrap={false}>
            <Text style={styles.sectionHeader}>{title}</Text>
        </View>
        {children}
    </View>
);

const ExperienceItem = ({ exp }) => {
    const bullets = splitBullets(exp.description);
    return (
        <View style={styles.item} wrap={false}>
            <View style={styles.itemHeader}>
                <Text style={styles.itemTitle}>{exp.role || ''}</Text>
                <Text style={styles.itemDate}>
                    {[exp.startDate, exp.endDate].filter(Boolean).join(' - ')}
                </Text>
            </View>
            {(exp.company || exp.location) && (
                <Text style={styles.itemSub}>
                    {exp.company}{exp.location ? `  •  ${exp.location}` : ''}
                </Text>
            )}
            {bullets.length > 0 && bullets.map((b, i) => (
                <Text key={i} style={styles.itemDesc}>
                    {b.startsWith('•') ? b : `• ${b.replace(/^[-*·]\s*/, '')}`}
                </Text>
            ))}
        </View>
    );
};

const EducationItem = ({ edu }) => (
    <View style={styles.item} wrap={false}>
        <View style={styles.itemHeader}>
            <Text style={styles.itemTitle}>{edu.school || ''}</Text>
            <Text style={styles.itemDate}>{edu.year || ''}</Text>
        </View>
        <Text style={styles.itemDesc}>
            {edu.degree}{edu.location ? `  •  ${edu.location}` : ''}
        </Text>
    </View>
);

const CustomSectionItem = ({ item }) => (
    <View style={styles.item} wrap={false}>
        <View style={styles.itemHeader}>
            <Text style={styles.itemTitle}>{item.title || ''}</Text>
            {item.subtitle && <Text style={styles.itemDate}>{item.subtitle}</Text>}
        </View>
        {item.subtext && <Text style={styles.itemSub}>{item.subtext}</Text>}
        {item.description && (
            splitBullets(item.description).map((b, i) => (
                <Text key={i} style={styles.itemDesc}>
                    {b.startsWith('•') ? b : `• ${b.replace(/^[-*·]\s*/, '')}`}
                </Text>
            ))
        )}
    </View>
);

const ModernPdf = ({ resumeData }) => {
    const p = resumeData.personal || {};
    const sectionOrder = resumeData.sectionOrder || ['experience', 'education', 'skills'];

    return (
        <Document>
            <Page size="A4" style={styles.page}>
                {/* Header */}
                <View style={styles.headerBlock}>
                    <Text style={styles.name}>{p.fullName || 'Your Name'}</Text>
                    <View style={styles.contactRow}>
                        {p.email && <Text style={styles.contactItem}>{p.email}</Text>}
                        {p.phone && <Text style={styles.contactItem}>• {p.phone}</Text>}
                        {p.location && <Text style={styles.contactItem}>• {p.location}</Text>}
                    </View>
                </View>

                {/* Summary */}
                {p.summary && (
                    <View style={styles.section}>
                        <Text style={styles.sectionHeader}>Professional Profile</Text>
                        <Text style={styles.summary}>{p.summary}</Text>
                    </View>
                )}

                {/* Dynamic sections */}
                {sectionOrder.map((sectionId) => {
                    if (sectionId === 'experience' && resumeData.experience?.length > 0) {
                        return (
                            <Section key="exp" title="Work Experience">
                                {resumeData.experience.map(e => <ExperienceItem key={e.id} exp={e} />)}
                            </Section>
                        );
                    }
                    if (sectionId === 'education' && resumeData.education?.length > 0) {
                        return (
                            <Section key="edu" title="Education">
                                {resumeData.education.map(e => <EducationItem key={e.id} edu={e} />)}
                            </Section>
                        );
                    }
                    if (sectionId === 'skills' && resumeData.skills?.length > 0) {
                        return (
                            <Section key="skills" title="Skills">
                                <View style={styles.skillsRow}>
                                    {resumeData.skills.map((s, i) => (
                                        <Text key={i} style={styles.skillChip}>{s}</Text>
                                    ))}
                                </View>
                            </Section>
                        );
                    }
                    if (!['experience', 'education', 'skills'].includes(sectionId)) {
                        // Ids survive JSON round-trips as either numbers or
                        // strings — compare loosely so custom sections are
                        // never silently dropped from the export.
                        const section = (resumeData.customSections || []).find(s => String(s.id) === String(sectionId));
                        if (section && section.items?.length > 0) {
                            return (
                                <Section key={sectionId} title={section.title}>
                                    {section.items.map(it => <CustomSectionItem key={it.id} item={it} />)}
                                </Section>
                            );
                        }
                    }
                    return null;
                })}
            </Page>
        </Document>
    );
};

export default ModernPdf;
